function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { Transaction } from './transaction';
/**
 * A handler's return is only an authoritative server entity if it is a real
 * object. Some endpoints (e.g. the task "mini" update) resolve with an empty
 * body (`""`) or no content; committing that as the entity would clobber the
 * store and clear the optimistic overlay, reverting the visible value to the
 * stale synced one. Treat any non-object return as "no server value — keep the
 * optimistic/merged value".
 */ const isEntity = (v)=>v != null && typeof v === 'object';
/**
 * Run `cb` AFTER the next browser paint.
 *
 * Notifications are already deferred off the mutation's call stack, but a bare
 * `setTimeout(0)` macrotask can still run BEFORE the next paint — so the
 * subscriber cascade (which includes `useSyncExternalStore`/`useCollectionitem`
 * re-renders that React runs SYNCHRONOUSLY and cannot defer) preempts the paint
 * of whatever the interaction just changed (e.g. an editor/popover closing).
 * That makes an optimistic edit feel UI-blocking: the editor visibly closes
 * only after the cascade finishes.
 *
 * `requestAnimationFrame` fires just before a paint; scheduling a macrotask
 * from inside it runs the cascade just AFTER that frame paints — so the
 * interaction (editor close) paints first and the heavy re-render work happens
 * on the next turn. Falls back to a plain macrotask where rAF is unavailable
 * (node / SSR / tests, where there is no paint to wait for).
 *
 * HIDDEN TABS: browsers do not fire `requestAnimationFrame` in a backgrounded
 * tab. Waiting for a paint that will never come strands the whole notification
 * queue — `pendingChangeIds` grows unbounded while the tab is hidden and every
 * subscriber (React re-renders included) stalls until the user returns, at
 * which point one enormous flush lands at once. A hidden tab also has no paint
 * to protect, which is the only reason to wait in the first place — so use a
 * plain macrotask whenever the document is hidden.
 */ const pendingPaintFlushes = new Set();
const isHidden = ()=>typeof document !== 'undefined' && document.visibilityState === 'hidden';
// A flush scheduled while VISIBLE, on a tab that is then backgrounded, is
// waiting on an rAF that will not fire until the tab is shown again — so drain
// anything already queued the moment we go hidden. One module-level listener
// covers every collection; per-instance listeners would be N× the work for the
// same signal.
if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', ()=>{
        if (!isHidden()) {
            return;
        }
        pendingPaintFlushes.forEach((cb)=>{
            pendingPaintFlushes.delete(cb);
            setTimeout(cb, 0);
        });
    });
}
const scheduleAfterPaint = (cb)=>{
    if (isHidden() || typeof requestAnimationFrame !== 'function') {
        setTimeout(cb, 0);
        return;
    }
    pendingPaintFlushes.add(cb);
    requestAnimationFrame(()=>{
        // Already drained by the visibilitychange handler — don't run twice.
        if (!pendingPaintFlushes.delete(cb)) {
            return;
        }
        setTimeout(cb, 0);
    });
};
/**
 * A normalized, push-based, in-memory store for a single entity type.
 *
 * - `push()` feeds server data in (from API calls, AgGrid pages, WS refetches).
 * - `insert/update/delete` apply optimistic changes and persist via handlers.
 * - reads (`get`) overlay optimistic state on top of the synced backing store.
 * - `subscribeToId` gives O(1), per-record reactivity.
 * - `purge` frees memory, deferring removal while listeners or in-flight
 *   transactions still reference an entry.
 */ export class Collection {
    stampWrite(id, isInsert) {
        this.writeSeq += 1;
        this.lastWriteSeq.set(id, this.writeSeq);
        if (isInsert) {
            this.lastInsertSeq.set(id, this.writeSeq);
            this.lastDeleteSeq.delete(id);
        }
    }
    /** Seq at which this id's current incarnation was inserted (0 = never). */ getLastInsertSeq(id) {
        var _this_lastInsertSeq_get;
        return (_this_lastInsertSeq_get = this.lastInsertSeq.get(id)) !== null && _this_lastInsertSeq_get !== void 0 ? _this_lastInsertSeq_get : 0;
    }
    /** Deletes clear their stamp (a re-add re-stamps), keeping the map bounded
   *  by live entries. */ clearWriteStamp(id) {
        this.lastWriteSeq.delete(id);
        this.lastInsertSeq.delete(id);
    }
    getLastDeleteSeq(id) {
        var _this_lastDeleteSeq_get;
        return (_this_lastDeleteSeq_get = this.lastDeleteSeq.get(id)) !== null && _this_lastDeleteSeq_get !== void 0 ? _this_lastDeleteSeq_get : 0;
    }
    stampDelete(id) {
        this.writeSeq += 1;
        this.lastDeleteSeq.set(id, this.writeSeq);
    }
    /** Current global write sequence — snapshot before a server round-trip. */ getWriteSeq() {
        return this.writeSeq;
    }
    /** Sequence of the last synced-store write for this id (0 = never). */ getLastWriteSeq(id) {
        var _this_lastWriteSeq_get;
        return (_this_lastWriteSeq_get = this.lastWriteSeq.get(id)) !== null && _this_lastWriteSeq_get !== void 0 ? _this_lastWriteSeq_get : 0;
    }
    // ─── Secondary indexes ─────────────────────────────────────────────────
    /** Normalize an index fn's return to a flat key list. */ static keysOf(raw) {
        if (raw == null) return [];
        return Array.isArray(raw) ? raw : [
            raw
        ];
    }
    /**
   * Re-file `id` from `oldEntity`'s keys to `newEntity`'s keys across every
   * index. Pass `undefined` for an insert (`oldEntity`) or a delete
   * (`newEntity`). No-op when no indexes are configured.
   */ reindex(id, oldEntity, newEntity) {
        if (this.indexDefs.length === 0) return;
        for (const def of this.indexDefs){
            const map = this.indexMaps.get(def.name);
            if (oldEntity !== undefined) {
                for (const key of Collection.keysOf(def.fn(oldEntity))){
                    const set = map.get(key);
                    if (set) {
                        set.delete(id);
                        if (set.size === 0) map.delete(key);
                    }
                }
            }
            if (newEntity !== undefined) {
                for (const key of Collection.keysOf(def.fn(newEntity))){
                    let set = map.get(key);
                    if (!set) {
                        set = new Set();
                        map.set(key, set);
                    }
                    set.add(id);
                }
            }
        }
    }
    /**
   * O(result) lookup of all VISIBLE entities filed under `key` in index `name`.
   * Applies the optimistic overlay (skips optimistically-deleted ids). Returns
   * `[]` for an unknown index or empty key. Replaces `getAll().filter(...)` for
   * the indexed field — O(matches) instead of O(n).
   */ getByIndex(name, key) {
        const map = this.indexMaps.get(name);
        if (!map) return [];
        const ids = map.get(key);
        if (!ids) return [];
        const out = [];
        ids.forEach((id)=>{
            const value = this.get(id);
            if (value !== undefined) out.push(value);
        });
        return out;
    }
    // ─── Reads (O(1)) ──────────────────────────────────────────────────────
    /** Effective value: optimistic overlay over synced backing store. */ get(id) {
        const o = this.overlay.get(id);
        if (o) {
            return o.kind === 'delete' ? undefined : o.value;
        }
        return this.synced.get(id);
    }
    /**
   * Server-confirmed value, ignoring the optimistic overlay — merges built
   * on `get()` would write un-committed values into the backing store.
   */ getSynced(id) {
        return this.synced.get(id);
    }
    has(id) {
        return this.get(id) !== undefined;
    }
    /** This collection's key for `entity`. Public so generic adapters (cross-tab
   *  sync) can key entities they only hold as `T`. */ keyOf(entity) {
        return this.getKey(entity);
    }
    /** Number of visible entities. */ size() {
        let count = this.synced.size;
        this.overlay.forEach((entry, key)=>{
            if (entry.kind === 'insert' && !this.synced.has(key)) {
                count += 1;
            } else if (entry.kind === 'delete' && this.synced.has(key)) {
                count -= 1;
            }
        });
        return count;
    }
    /** Snapshot of all visible entities. O(n) — prefer `get` for single reads. */ getAll() {
        const result = [];
        this.synced.forEach((value, key)=>{
            const o = this.overlay.get(key);
            if (o) {
                if (o.kind !== 'delete') {
                    result.push(o.value);
                }
                return;
            }
            result.push(value);
        });
        // overlay-only inserts (not yet in synced)
        this.overlay.forEach((entry, key)=>{
            if (entry.kind === 'insert' && !this.synced.has(key)) {
                result.push(entry.value);
            }
        });
        return result;
    }
    getState(id) {
        const o = this.overlay.get(id);
        if (o) {
            if (o.kind === 'insert') return 'creating';
            if (o.kind === 'update') return 'updating';
            return 'deleting';
        }
        return this.synced.has(id) ? 'synced' : undefined;
    }
    // ─── Data intake ───────────────────────────────────────────────────────
    /**
   * Feed server-shaped entities into the backing store. Deduplicates by key
   * and applies the `isNewer` staleness check. Entries with an active
   * optimistic overlay keep their overlay (visible value unchanged) while the
   * backing store is still refreshed underneath.
   *
   * `opts.mode`:
   * - `'replace'` (default) — the incoming entity wholly replaces the synced
   *   value.
   * - `'merge'` — shallow-overlay the incoming fields onto the existing synced
   *   value (`{ ...existing, ...incoming }`). Use for partial/lower-fidelity
   *   sources (a light list landing over a full detail record) so absent fields
   *   are NOT dropped. The merged result is still subject to `isNewer`.
   */ push(entities, opts) {
        // Not every source hands us a list. Plenty of endpoints return a keyed
        // object (`{ [id]: entity }`, a settings blob, a config map) and callers
        // were pre-wrapping with `Object.values(...)` — which, on a response that
        // omitted the field, threw before it ever reached us. Normalizing here
        // makes the guard the collection's job instead of every call site's.
        const list = entities == null ? [] : Array.isArray(entities) ? entities : Object.values(entities);
        if (list.length === 0) {
            return;
        }
        const merge = (opts === null || opts === void 0 ? void 0 : opts.mode) === 'merge';
        // Bulk fast path: building (and later iterating) an N-entry `changed` Set
        // is pure waste on a cold load — the common case is a list/detail fetch
        // landing BEFORE any component has subscribed. Only track changed ids when
        // something is actually listening. (Per-id `idListeners` are checked per
        // entry below; `changeListeners` decide whether we track at all.)
        const track = this.changeListeners.size > 0 || this.idListeners.size > 0;
        const changed = track ? new Set() : null;
        const hasIndexes = this.indexDefs.length > 0;
        // Same fast-path logic as `changed`: only materialize the write-event
        // payload when something is actually observing writes. A cold 1M-entity
        // load must not pay for an array nobody reads.
        const written = this.writeListeners.size > 0 ? [] : null;
        const created = [];
        for (const incoming of list){
            const key = this.getKey(incoming);
            const existing = this.synced.get(key);
            // In merge mode, overlay the incoming partial onto the existing record so
            // fields the lower-fidelity source omits are preserved.
            const entity = merge && existing !== undefined ? {
                ...existing,
                ...incoming
            } : incoming;
            if (existing !== undefined && !this.isNewer(entity, existing)) {
                continue;
            }
            const hadEntry = this.synced.has(key);
            this.synced.set(key, entity);
            this.stampWrite(key, !hadEntry);
            if (hasIndexes) {
                this.reindex(key, existing, entity);
            }
            // NOTE: deliberately does NOT bump `syncedSeq`. A WS refetch that races
            // an in-flight mutation can carry pre-mutation (stale) state; letting it
            // win would re-introduce the very flicker we're removing. While the
            // mutation is in flight the overlay masks this value anyway; once the
            // mutation settles, the seq guard in `commitUpdate` keeps the newest
            // optimistic/server value. A WS refetch that lands AFTER settle (no
            // overlay) updates the visible value normally.
            // The visible value only changed if no overlay is masking this id.
            if (changed && !this.overlay.has(key)) {
                changed.add(key);
            }
            if (written) {
                written.push(entity);
                if (!hadEntry) {
                    created.push(key);
                }
            }
        }
        if (changed) {
            this.notify(changed);
        }
        if (written) {
            var _ref;
            this.emitWrite({
                origin: 'push',
                // `source` is required by the type, but stay defensive at runtime:
                // untyped JS callers still reach this, and a crash here would take
                // down an ingest path over a missing label.
                source: (_ref = opts === null || opts === void 0 ? void 0 : opts.source) !== null && _ref !== void 0 ? _ref : 'fetch',
                upserts: written,
                deletes: [],
                created
            });
        }
    }
    /**
   * Remove an entity that has already been deleted on the server (WS event or
   * a confirmed delete call). Unlike `delete()`, this does NOT run the
   * `onDelete` handler — the server change is authoritative — and removes the
   * entry immediately regardless of listeners.
   *
   * `opts.source` defaults to `'ws'`, the dominant caller. That default matters
   * for cross-tab sync: every tab receives the socket event independently, so a
   * propagated `ws` removal would be redundant N times over. Callers removing
   * an entity because THIS tab just deleted it server-side should pass
   * `{ source: 'mutation' }` so peers learn about it.
   */ externalDelete(id, opts = {}) {
        const existed = this.synced.has(id) || this.overlay.has(id);
        this.reindex(id, this.synced.get(id), undefined);
        this.synced.delete(id);
        this.clearWriteStamp(id);
        this.stampDelete(id);
        this.overlay.delete(id);
        this.overlaySeq.delete(id);
        this.syncedSeq.delete(id);
        if (existed) {
            var _opts_source;
            this.notify(new Set([
                id
            ]));
            this.emitWrite({
                origin: 'external-delete',
                source: (_opts_source = opts.source) !== null && _opts_source !== void 0 ? _opts_source : 'ws',
                upserts: [],
                deletes: [
                    id
                ]
            });
        }
    }
    // ─── Optimistic mutations ──────────────────────────────────────────────
    insert(data, opts = {}) {
        var _opts_optimistic;
        const key = this.getKey(data);
        const optimistic = (_opts_optimistic = opts.optimistic) !== null && _opts_optimistic !== void 0 ? _opts_optimistic : true;
        if (optimistic) {
            this.overlay.set(key, {
                kind: 'insert',
                value: data
            });
            this.notify(new Set([
                key
            ]));
            this.emitWrite({
                origin: 'optimistic',
                upserts: [
                    data
                ],
                deletes: [],
                created: [
                    key
                ]
            });
        }
        this.notifyMutation(new Set([
            key
        ]));
        const ctx = {
            id: key,
            type: 'insert',
            modified: data,
            metadata: opts.metadata
        };
        return this.runHandler(key, this.options.onInsert, ctx, {
            onRollback: ()=>{
                if (optimistic) {
                    this.overlay.delete(key);
                    this.notify(new Set([
                        key
                    ]));
                    // Phantom insert — tell peers to drop it.
                    this.emitWrite({
                        origin: 'rollback',
                        upserts: [],
                        deletes: [
                            key
                        ]
                    });
                }
            },
            onCommit: (returned)=>this.commitInsert(key, data, returned, optimistic)
        });
    }
    update(id, changes, opts = {}) {
        var _opts_optimistic;
        const original = this.get(id);
        if (original === undefined) {
            const tx = new Transaction(()=>undefined);
            tx.fail(new Error(`update: no entity for key ${String(id)}`));
            return tx;
        }
        const optimistic = (_opts_optimistic = opts.optimistic) !== null && _opts_optimistic !== void 0 ? _opts_optimistic : true;
        // Compute the optimistic overlay. Defaults to a shallow merge; a collection
        // can supply `mergeChanges` to merge nested collections element-wise (e.g.
        // custom `fields[]` by id) instead of replacing them wholesale.
        const merged = this.applyChanges(original, changes);
        // Stamp this mutation so overlapping updates to the same id stay
        // flicker-free regardless of server-response ordering.
        const seq = this.seqCounter += 1;
        if (optimistic) {
            this.overlay.set(id, {
                kind: 'update',
                value: merged
            });
            this.overlaySeq.set(id, seq);
            // Re-file `id` into its new index bucket(s) NOW, not on server commit —
            // otherwise an index-backed read (`getByIndex`, e.g. a phase swimlane's
            // task list) keeps the id in its OLD bucket until the round-trip
            // settles, even though `get(id)` already reflects the new value.
            // `reindex` only touches the specific keys `original`/`merged` derive
            // (O(1) per index), not a full index rebuild.
            this.reindex(id, original, merged);
            this.notify(new Set([
                id
            ]));
            this.emitWrite({
                origin: 'optimistic',
                upserts: [
                    merged
                ],
                deletes: []
            });
        }
        this.notifyMutation(new Set([
            id
        ]));
        const ctx = {
            id,
            type: 'update',
            changes,
            original,
            modified: merged,
            metadata: opts.metadata
        };
        return this.runHandler(id, this.options.onUpdate, ctx, {
            onRollback: ()=>this.rollbackUpdate(id, seq, optimistic),
            onCommit: (returned)=>this.commitUpdate(id, seq, changes, merged, returned)
        });
    }
    delete(id, opts = {}) {
        var _opts_optimistic;
        const original = this.get(id);
        if (original === undefined) {
            const tx = new Transaction(()=>undefined);
            tx.fail(new Error(`delete: no entity for key ${String(id)}`));
            return tx;
        }
        const optimistic = (_opts_optimistic = opts.optimistic) !== null && _opts_optimistic !== void 0 ? _opts_optimistic : true;
        if (optimistic) {
            this.overlay.set(id, {
                kind: 'delete'
            });
            this.notify(new Set([
                id
            ]));
            // Peers hide the row now rather than a round-trip later — the same deal
            // `insert`/`update` get. Without this a delete was optimistic locally and
            // commit-only for everyone else.
            this.emitWrite({
                origin: 'optimistic',
                upserts: [],
                deletes: [
                    id
                ]
            });
        }
        this.notifyMutation(new Set([
            id
        ]));
        const ctx = {
            id,
            type: 'delete',
            original,
            metadata: opts.metadata
        };
        return this.runHandler(id, this.options.onDelete, ctx, {
            onRollback: ()=>{
                if (optimistic) {
                    this.overlay.delete(id);
                    this.notify(new Set([
                        id
                    ]));
                    // Restore the row on every peer that hid it. `created` is required,
                    // not decorative: a peer applied the optimistic delete by REMOVING the
                    // entity, so it no longer has the key — and an upsert for an unknown
                    // key is filtered out unless it is declared a creation. Without the
                    // flag the revert is silently dropped and the row stays gone there.
                    this.emitWrite({
                        origin: 'rollback',
                        upserts: [
                            original
                        ],
                        deletes: [],
                        created: [
                            id
                        ]
                    });
                }
            },
            onCommit: ()=>{
                this.reindex(id, this.synced.get(id), undefined);
                this.synced.delete(id);
                this.clearWriteStamp(id);
                this.stampDelete(id);
                this.overlay.delete(id);
                this.notify(new Set([
                    id
                ]));
                this.emitWrite({
                    origin: 'mutation',
                    upserts: [],
                    deletes: [
                        id
                    ]
                });
            }
        });
    }
    /**
   * Apply multiple optimistic mutations as ONE transaction, persisted by a
   * single `mutationFn` (e.g. a bulk endpoint). All overlays are applied
   * immediately; on failure they all roll back together.
   *
   * Use for bulk edits, multi-task dependency updates, or any change that the
   * backend accepts as one atomic call. `mutationFn` may return the
   * server-confirmed entities to commit; otherwise the optimistic values are
   * committed.
   */ batch(build, mutationFn) {
        const mutations = [];
        const affected = new Set();
        // Snapshot prior overlay per id so rollback is exact.
        const prevOverlay = new Map();
        const setOverlay = (id, entry)=>{
            if (!prevOverlay.has(id)) {
                prevOverlay.set(id, this.overlay.get(id));
            }
            this.overlay.set(id, entry);
            affected.add(id);
        };
        const recorder = {
            insert: (data)=>{
                const id = this.getKey(data);
                setOverlay(id, {
                    kind: 'insert',
                    value: data
                });
                mutations.push({
                    type: 'insert',
                    id,
                    modified: data
                });
            },
            update: (id, changes)=>{
                const original = this.get(id);
                if (original === undefined) {
                    return;
                }
                const merged = this.applyChanges(original, changes);
                setOverlay(id, {
                    kind: 'update',
                    value: merged
                });
                mutations.push({
                    type: 'update',
                    id,
                    changes,
                    original,
                    modified: merged
                });
            },
            remove: (id)=>{
                const original = this.get(id);
                if (original === undefined) {
                    return;
                }
                setOverlay(id, {
                    kind: 'delete'
                });
                mutations.push({
                    type: 'delete',
                    id,
                    original
                });
            }
        };
        build(recorder);
        this.notify(affected);
        this.notifyMutation(affected);
        const rollback = ()=>{
            prevOverlay.forEach((entry, id)=>{
                if (entry === undefined) {
                    this.overlay.delete(id);
                } else {
                    this.overlay.set(id, entry);
                }
            });
            this.notify(affected);
        };
        const tx = new Transaction(rollback);
        affected.forEach((id)=>this.trackTx(id, tx));
        const untrackAll = ()=>{
            affected.forEach((id)=>this.untrackTx(id, tx));
        };
        if (mutations.length === 0) {
            untrackAll();
            tx.complete();
            return tx;
        }
        tx.markPersisting();
        Promise.resolve().then(()=>mutationFn({
                mutations
            })).then((returned)=>{
            if (tx.state === 'failed') {
                untrackAll();
                return;
            }
            this.commitBatch(mutations, returned !== null && returned !== void 0 ? returned : undefined);
            untrackAll();
            tx.complete();
        }).catch((error)=>{
            if (tx.state !== 'failed') {
                rollback();
                untrackAll();
                tx.fail(error);
            } else {
                untrackAll();
            }
        });
        return tx;
    }
    // ─── Memory management ─────────────────────────────────────────────────
    /** Remove all entries matching the predicate (deferred where referenced). */ purge(predicate) {
        const candidates = [];
        this.synced.forEach((value, key)=>{
            if (predicate(value)) {
                candidates.push(key);
            }
        });
        this.overlay.forEach((entry, key)=>{
            if (entry.kind !== 'delete' && !this.synced.has(key) && predicate(entry.value)) {
                candidates.push(key);
            }
        });
        candidates.forEach((key)=>this.requestPurge(key));
    }
    purgeById(ids) {
        ids.forEach((id)=>this.requestPurge(id));
    }
    /** Wipe everything. Ignores listeners and in-flight transactions. */ clear() {
        const changed = new Set([
            ...this.synced.keys(),
            ...this.overlay.keys()
        ]);
        this.synced.clear();
        this.lastWriteSeq.clear();
        this.lastInsertSeq.clear();
        this.lastDeleteSeq.clear();
        this.overlay.clear();
        this.overlaySeq.clear();
        this.syncedSeq.clear();
        this.pendingPurge.clear();
        this.indexMaps.forEach((map)=>map.clear());
        this.notify(changed);
        // `origin: 'clear'` — local teardown, NOT a statement that the server
        // deleted anything. Observers (cross-tab sync) must never turn this into
        // deletes elsewhere.
        this.emitWrite({
            origin: 'clear',
            upserts: [],
            deletes: [
                ...changed
            ]
        });
    }
    // ─── Subscriptions ─────────────────────────────────────────────────────
    /** Subscribe to a single id. Fires only when that id's value changes. */ subscribeToId(id, listener) {
        let set = this.idListeners.get(id);
        if (!set) {
            set = new Set();
            this.idListeners.set(id, set);
        }
        set.add(listener);
        return ()=>{
            const s = this.idListeners.get(id);
            if (!s) return;
            s.delete(listener);
            if (s.size === 0) {
                this.idListeners.delete(id);
            }
            this.maybePurge(id);
        };
    }
    /** Subscribe to all changes; receives the set of changed ids. */ subscribeToChanges(listener) {
        this.changeListeners.add(listener);
        return ()=>{
            this.changeListeners.delete(listener);
        };
    }
    /**
   * Subscribe to optimistic mutations only (`insert`/`update`/`delete`). Does
   * NOT fire for `push` (hydration / WS refetch). See `mutationListeners`.
   */ subscribeToMutations(listener) {
        this.mutationListeners.add(listener);
        return ()=>{
            this.mutationListeners.delete(listener);
        };
    }
    /**
   * Observe SYNCED-store writes synchronously, with provenance. See
   * `writeListeners`. Keep handlers cheap — these run inline on the ingest hot
   * path (a bulk `push` of 100k entities calls this once, but with a 100k-entry
   * `upserts` array).
   */ subscribeToWrites(listener) {
        this.writeListeners.add(listener);
        return ()=>{
            this.writeListeners.delete(listener);
        };
    }
    /** Emit a write event. Zero-cost when nothing is observing. */ emitWrite(event) {
        if (this.writeListeners.size === 0) {
            return;
        }
        if (event.upserts.length === 0 && event.deletes.length === 0) {
            return;
        }
        this.writeListeners.forEach((fn)=>fn(event));
    }
    // ─── Internals ─────────────────────────────────────────────────────────
    notify(changedIds) {
        if (changedIds.size === 0) {
            return;
        }
        changedIds.forEach((id)=>this.pendingChangeIds.add(id));
        this.scheduleFlush();
    }
    /** Coalesce + flush notifications on a macrotask (see field docs). */ scheduleFlush() {
        if (this.flushScheduled) {
            return;
        }
        this.flushScheduled = true;
        // Run the subscriber cascade AFTER the next paint (see `scheduleAfterPaint`)
        // so the interaction that triggered the mutation — e.g. an editor/popover
        // closing — paints first instead of being blocked behind the cascade.
        scheduleAfterPaint(()=>this.flushNotifications());
    }
    flushNotifications() {
        this.flushScheduled = false;
        if (this.pendingChangeIds.size > 0) {
            const changedIds = this.pendingChangeIds;
            this.pendingChangeIds = new Set();
            changedIds.forEach((id)=>{
                const set = this.idListeners.get(id);
                if (set) {
                    set.forEach((fn)=>fn());
                }
            });
            this.changeListeners.forEach((fn)=>fn(changedIds));
        }
        if (this.pendingMutationIds.size > 0 && this.mutationListeners.size > 0) {
            const mutatedIds = this.pendingMutationIds;
            this.pendingMutationIds = new Set();
            this.mutationListeners.forEach((fn)=>fn(mutatedIds));
        } else {
            this.pendingMutationIds = new Set();
        }
    }
    /** Notify mutation-only listeners (optimistic insert/update/delete). */ notifyMutation(changedIds) {
        if (changedIds.size === 0 || this.mutationListeners.size === 0) {
            return;
        }
        changedIds.forEach((id)=>this.pendingMutationIds.add(id));
        this.scheduleFlush();
    }
    trackTx(id, tx) {
        let set = this.pendingTx.get(id);
        if (!set) {
            set = new Set();
            this.pendingTx.set(id, set);
        }
        set.add(tx);
    }
    untrackTx(id, tx) {
        const set = this.pendingTx.get(id);
        if (!set) return;
        set.delete(tx);
        if (set.size === 0) {
            this.pendingTx.delete(id);
        }
        this.maybePurge(id);
    }
    hasListeners(id) {
        const set = this.idListeners.get(id);
        return !!set && set.size > 0;
    }
    hasPendingTx(id) {
        const set = this.pendingTx.get(id);
        return !!set && set.size > 0;
    }
    /** Mark an id for purge; remove immediately if unreferenced. */ requestPurge(id) {
        this.pendingPurge.add(id);
        this.maybePurge(id);
    }
    /** Remove an id if it is marked for purge and no longer referenced. */ maybePurge(id) {
        if (!this.pendingPurge.has(id)) {
            return;
        }
        if (this.hasListeners(id) || this.hasPendingTx(id)) {
            return;
        }
        this.pendingPurge.delete(id);
        const existed = this.synced.has(id) || this.overlay.has(id);
        this.reindex(id, this.synced.get(id), undefined);
        this.synced.delete(id);
        this.clearWriteStamp(id);
        this.overlay.delete(id);
        this.overlaySeq.delete(id);
        this.syncedSeq.delete(id);
        if (existed) {
            this.notify(new Set([
                id
            ]));
            // `origin: 'purge'` — memory eviction. The entity still exists on the
            // server, so this must never propagate as a deletion.
            this.emitWrite({
                origin: 'purge',
                upserts: [],
                deletes: [
                    id
                ]
            });
        }
    }
    /**
   * Runs an operation handler with optimistic lifecycle management. The
   * handler persists to the backend; on success `onCommit` applies the
   * confirmed state, on failure `onRollback` reverts the optimistic overlay.
   */ runHandler(id, handler, ctx, hooks) {
        const tx = new Transaction(()=>hooks.onRollback());
        this.trackTx(id, tx);
        if (!handler) {
            // No persistence: commit the optimistic value immediately.
            hooks.onCommit(undefined);
            // untrack before completing so any deferred purge fires before
            // `isPersisted` awaiters observe the committed state.
            this.untrackTx(id, tx);
            tx.complete();
            return tx;
        }
        tx.markPersisting();
        Promise.resolve().then(()=>handler(ctx)).then((returned)=>{
            if (tx.state === 'failed') {
                this.untrackTx(id, tx); // rolled back during flight
                return;
            }
            hooks.onCommit(returned !== null && returned !== void 0 ? returned : undefined);
            this.untrackTx(id, tx);
            tx.complete();
        }).catch((error)=>{
            if (tx.state !== 'failed') {
                hooks.onRollback();
                this.untrackTx(id, tx);
                tx.fail(error);
            } else {
                this.untrackTx(id, tx);
            }
        });
        return tx;
    }
    commitInsert(tempKey, optimisticValue, returned, optimistic) {
        const changed = new Set();
        if (optimistic) {
            this.overlay.delete(tempKey);
            changed.add(tempKey);
        }
        let committed;
        let wasCreated;
        if (isEntity(returned)) {
            const realKey = this.getKey(returned);
            this.reindex(realKey, this.synced.get(realKey), returned);
            const hadEntry = this.synced.has(realKey);
            wasCreated = !hadEntry;
            this.synced.set(realKey, returned);
            this.stampWrite(realKey, !hadEntry);
            changed.add(realKey);
            // id swap: drop the temp entry entirely
            if (realKey !== tempKey) {
                this.reindex(tempKey, this.synced.get(tempKey), undefined);
                this.synced.delete(tempKey);
                this.clearWriteStamp(tempKey);
                this.overlay.delete(tempKey);
            }
            committed = returned;
        } else {
            // commit the optimistic value under its key
            this.reindex(tempKey, this.synced.get(tempKey), optimisticValue);
            const hadEntry = this.synced.has(tempKey);
            wasCreated = !hadEntry;
            this.synced.set(tempKey, optimisticValue);
            this.stampWrite(tempKey, !hadEntry);
            changed.add(tempKey);
            committed = optimisticValue;
        }
        this.notify(changed);
        const committedKey = this.getKey(committed);
        // Include the temp key as a delete when the server assigned a different id.
        // Before optimistic broadcasting this was a no-op (peers never saw the temp
        // key), but now peers receive the 'optimistic' envelope and must be told to
        // drop the placeholder when the real id arrives.
        const deletedTempKey = committedKey !== tempKey && optimistic ? [
            tempKey
        ] : [];
        this.emitWrite({
            origin: 'mutation',
            upserts: [
                committed
            ],
            deletes: deletedTempKey,
            created: wasCreated ? [
                committedKey
            ] : []
        });
    }
    commitBatch(mutations, returned) {
        const changed = new Set();
        const upserts = [];
        const deletes = [];
        // A batch may mix inserts and updates; report only the genuinely new keys.
        const insertedIds = new Set(mutations.filter((m)=>m.type === 'insert').map((m)=>m.id));
        const created = [];
        if (returned) {
            returned.forEach((entity)=>{
                if (!isEntity(entity)) {
                    return;
                }
                const key = this.getKey(entity);
                this.reindex(key, this.synced.get(key), entity);
                const hadEntry = this.synced.has(key);
                this.synced.set(key, entity);
                this.stampWrite(key, !hadEntry);
                changed.add(key);
                upserts.push(entity);
                if (!hadEntry) {
                    created.push(key);
                }
            });
        }
        mutations.forEach((m)=>{
            this.overlay.delete(m.id);
            changed.add(m.id);
            if (m.type === 'delete') {
                this.reindex(m.id, this.synced.get(m.id), undefined);
                this.synced.delete(m.id);
                this.clearWriteStamp(m.id);
                this.stampDelete(m.id);
                deletes.push(m.id);
            } else if (!returned && m.modified !== undefined) {
                // No server payload: commit the optimistic value.
                this.reindex(m.id, this.synced.get(m.id), m.modified);
                const hadEntry = this.synced.has(m.id);
                this.synced.set(m.id, m.modified);
                this.stampWrite(m.id, !hadEntry);
                upserts.push(m.modified);
                if (!hadEntry && insertedIds.has(m.id)) {
                    created.push(m.id);
                }
            }
        });
        this.notify(changed);
        this.emitWrite({
            origin: 'mutation',
            upserts,
            deletes,
            created
        });
    }
    /**
   * Commit one optimistic update. `seq` orders overlapping updates to the same
   * id so that out-of-order server responses cannot flicker the visible value:
   *
   * - The synced backing store only accepts this value if `seq` is at least as
   *   new as the last value written there (a late response from an older
   *   update never clobbers a newer one).
   * - The optimistic overlay is cleared only when THIS commit corresponds to
   *   the latest optimistic intent (`overlaySeq === seq`). While newer updates
   *   are still in flight, the overlay keeps masking the backing store, so the
   *   user keeps seeing their most recent change until it too settles.
   */ /** Optimistic overlay value: custom `mergeChanges` or a shallow spread. */ applyChanges(original, changes) {
        return this.options.mergeChanges ? this.options.mergeChanges(original, changes) : {
            ...original,
            ...changes
        };
    }
    commitUpdate(id, seq, changes, merged, returned) {
        var _this_syncedSeq_get;
        const hasServerValue = isEntity(returned);
        // Void responses must re-merge the original patch onto the latest synced
        // record. Committing the pre-request `merged` snapshot would clobber
        // newer fields that arrived via push/socket while the request was in flight.
        const latestSynced = this.synced.get(id);
        const value = hasServerValue ? returned : latestSynced != null ? this.applyChanges(latestSynced, changes) : merged;
        const key = hasServerValue ? this.getKey(returned) : id;
        const accepted = seq >= ((_this_syncedSeq_get = this.syncedSeq.get(key)) !== null && _this_syncedSeq_get !== void 0 ? _this_syncedSeq_get : 0);
        if (accepted) {
            // Reindex from `merged` (the value `update()`'s optimistic apply already
            // re-filed the index to under `id`), not the pre-transaction synced
            // value — the index has been sitting on `merged`'s buckets since the
            // optimistic apply. Reindexing from the OLD synced value here would
            // leave a leaked bucket entry in the rare case the server's confirmed
            // value's index key differs from what was optimistically merged (Set
            // add/delete are idempotent, so the common same-key case is unaffected).
            if (key !== id) {
                // Server returned a different key than the one being updated (id
                // swap) — drop the optimistic entry filed under `id` before filing
                // the confirmed value under `key`, or `id` leaks in the index forever.
                this.reindex(id, merged, undefined);
            }
            this.reindex(key, merged, value);
            const hadEntry = this.synced.has(key);
            this.synced.set(key, value);
            this.stampWrite(key, !hadEntry);
            this.syncedSeq.set(key, seq);
        }
        if (this.overlaySeq.get(id) === seq) {
            this.overlay.delete(id);
            this.overlaySeq.delete(id);
        }
        this.notify(key === id ? new Set([
            id
        ]) : new Set([
            id,
            key
        ]));
        // Only report a write the seq guard actually accepted — a late response
        // from a superseded update wrote nothing, so propagating its stale value
        // would undo the newer one everywhere else.
        if (accepted) {
            // No delete is reported for an id swap (`key !== id`): that branch
            // re-files the index but leaves `synced[id]` in place, so reporting a
            // removal would describe a write that did not happen.
            this.emitWrite({
                origin: 'mutation',
                upserts: [
                    value
                ],
                deletes: []
            });
        }
    }
    /**
   * Roll back one optimistic update. Only the LATEST optimistic intent owns the
   * overlay, so an older mutation failing while a newer one is still in flight
   * must NOT drop the overlay — the newer change stays visible. When the latest
   * mutation is the one rolling back, the overlay is removed and the (last
   * committed) synced value becomes visible again.
   */ rollbackUpdate(id, seq, optimistic) {
        if (!optimistic) {
            return;
        }
        if (this.overlaySeq.get(id) === seq) {
            // Re-file back OUT of the optimistic bucket the matching `update()`
            // call filed `id` into — mirrors that call's `reindex(id, original,
            // merged)`. Read the about-to-be-reverted value before deleting the
            // overlay, and the post-rollback value after, so the reindex uses the
            // exact before/after pair (falls back to the synced value, or a
            // lower-seq overlay if one still exists underneath).
            const before = this.get(id);
            this.overlay.delete(id);
            this.overlaySeq.delete(id);
            this.reindex(id, before, this.get(id));
            this.notify(new Set([
                id
            ]));
            // Broadcast the server-confirmed (pre-mutation) value so peers revert
            // their own optimistic copy. `synced.get(id)` is the right value:
            // the synced store is never touched during an optimistic window, so it
            // holds whatever the server last confirmed — exactly what peers should
            // show while there is no pending local mutation.
            const reverted = this.synced.get(id);
            if (reverted !== undefined) {
                this.emitWrite({
                    origin: 'rollback',
                    upserts: [
                        reverted
                    ],
                    deletes: []
                });
            }
        }
    }
    constructor(options){
        var _options_isNewer;
        _define_property(this, "id", void 0);
        _define_property(this, "getKey", void 0);
        _define_property(this, "isNewer", void 0);
        _define_property(this, "options", void 0);
        /** Server-confirmed state. */ _define_property(this, "synced", new Map());
        /** Optimistic overlay for ids with in-flight mutations. */ _define_property(this, "overlay", new Map());
        /** Per-id listeners (reference counted). */ _define_property(this, "idListeners", new Map());
        /** Global change listeners; receive the set of changed ids. */ _define_property(this, "changeListeners", new Set());
        /**
   * Mutation listeners — fire ONLY for optimistic `insert`/`update`/`delete`
   * (a user-driven write), NOT for `push` (server hydration / WS refetch). Use
   * when a consumer must react to genuine edits but must NOT be woken by bulk
   * data loads (e.g. the kanban board re-laying out a single edited card —
   * subscribing to every change would turn a bulk list hydration into one
   * server request per task).
   */ _define_property(this, "mutationListeners", new Set());
        /**
   * Synced-store write observers. Fire SYNCHRONOUSLY with provenance — unlike
   * every other listener here, which is coalesced onto a deferred flush. Used
   * by cross-tab sync, which must know whether a write came from a local
   * mutation, a socket refetch, or another tab's broadcast in order to decide
   * whether to propagate it (and which, by the time the deferred flush runs,
   * is unknowable). Never fires for optimistic overlays — only committed state.
   */ _define_property(this, "writeListeners", new Set());
        /**
   * Notifications are coalesced and flushed on a macrotask, NOT synchronously.
   * The backing store / overlay are updated synchronously (so `get(id)` returns
   * the new value immediately — optimistic edits are instant), but the
   * subscriber cascade (per-id listeners, `subscribeToChanges`,
   * `subscribeToMutations` — which drive React re-renders, O(n) selects, and the
   * gantt sync) runs off the mutation's call stack. This keeps a `mutate()` from
   * blocking the UI thread (e.g. an editor blur) while the cascade runs, and
   * batches rapid successive edits into a single flush. Ordering of the
   * authoritative state (seq/overlay/commit) is unaffected — only notification
   * timing is deferred.
   */ _define_property(this, "pendingChangeIds", new Set());
        _define_property(this, "pendingMutationIds", new Set());
        _define_property(this, "flushScheduled", false);
        /** In-flight transactions per id. */ _define_property(this, "pendingTx", new Map());
        /** Ids marked for removal once listeners + transactions clear. */ _define_property(this, "pendingPurge", new Set());
        /**
   * Monotonic sequence stamped on each optimistic mutation. Used to keep
   * overlapping updates to the same id flicker-free: out-of-order server
   * responses must not expose an older value or clear a newer optimistic
   * overlay.
   */ _define_property(this, "seqCounter", 0);
        /** Seq of the latest optimistic overlay per id (the newest user intent). */ _define_property(this, "overlaySeq", new Map());
        /** Seq of the latest value written into the synced backing store per id. */ _define_property(this, "syncedSeq", new Map());
        /**
   * Bumped SYNCHRONOUSLY per synced-store write, with a per-id stamp —
   * read-then-write flows need this ordering (notifications coalesce).
   */ _define_property(this, "writeSeq", 0);
        _define_property(this, "lastWriteSeq", new Map());
        /**
   * Seq that FIRST filed this key: reconciles protect concurrent creates
   * while value overlays of existing ids don't shield deletions.
   */ _define_property(this, "lastInsertSeq", new Map());
        /** Seq of the last confirmed deletion per id (cleared on re-insert). Cache
   *  purges don't stamp — an evicted entry still exists server-side. */ _define_property(this, "lastDeleteSeq", new Map());
        /**
   * Secondary indexes: `indexName → (indexKey → Set<entityId>)`. Maintained
   * over the SYNCED store so `getByIndex` is O(result). Empty when no indexes
   * are configured (zero overhead on the hot ingest path).
   */ _define_property(this, "indexDefs", []);
        _define_property(this, "indexMaps", new Map());
        this.id = options.id;
        this.getKey = options.getKey;
        this.isNewer = (_options_isNewer = options.isNewer) !== null && _options_isNewer !== void 0 ? _options_isNewer : ()=>true;
        this.options = options;
        if (options.indexes) {
            for (const [name, fn] of Object.entries(options.indexes)){
                this.indexDefs.push({
                    name,
                    fn
                });
                this.indexMaps.set(name, new Map());
            }
        }
    }
}
/**
 * Every collection ever constructed.
 *
 * Exists so the mutation reporter can catch a write to a collection a mutation
 * never declared — the check is worthless if it can only see the collections
 * the author already remembered to list. Collections are module singletons
 * that live for the page's lifetime, so holding them here costs nothing and
 * frees nothing to release.
 */ const allCollections = new Set();
/** Snapshot of every constructed collection. Dev tooling and tests only. */ export const getAllCollections = ()=>[
        ...allCollections
    ];
export function createCollection(options) {
    const collection = new Collection(options);
    allCollections.add(collection);
    return collection;
}
